import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import { PrefillModel } from '../../models/prefill.model';
import {
  ALLOWED_FILE_ACCEPT,
  isPreviewableImage,
  validateUploadFile,
} from '../../models/presign-file.model';
import { TemplateModel } from '../../models/template.model';

/** Lo que resuelve el modal al guardar. */
export interface IPrefillModalResult {
  templateId: string;
  name: string;
  status: boolean;
  /** Archivo elegido, o `null` si no se toco la imagen. */
  file: File | null;
  /** MIME del archivo. Va en el body para que el API devuelva el presignado. */
  contentType: string | null;
}

/**
 * Alta y edicion de un prerellenado.
 *
 * La plantilla solo se elige al crear: el PUT del API no acepta `templateId`, y
 * cambiarla dejaria los valores existentes apuntando a campos de otra plantilla.
 */
@Component({
  selector: 'app-prefill-modal',
  imports: [NgSelectComponent, ReactiveFormsModule],
  templateUrl: './prefill-modal.component.html',
  styleUrl: './prefill-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrefillModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly formBuilder = inject(FormBuilder);

  protected readonly accept = ALLOWED_FILE_ACCEPT;

  /** `null` = alta. Con modelo = edicion. */
  protected readonly prefill = signal<PrefillModel | null>(null);

  /** Plantillas ACTIVAS: el API rechaza con 400 las inactivas. */
  protected readonly templates = signal<TemplateModel[]>([]);

  protected readonly isEdit = computed(() => this.prefill() !== null);

  protected readonly title = computed(() =>
    this.isEdit() ? 'Editar prerellenado' : 'Nuevo prerellenado',
  );

  /** Nombre de la plantilla asociada, para mostrarla en solo lectura al editar. */
  protected readonly templateName = computed(() => {
    const prefill = this.prefill();
    if (!prefill) return '';
    const template = this.templates().find((item) => item.templateId === prefill.templateId);
    return template ? `${template.name} (${template.code})` : prefill.templateId;
  });

  /**
   * Lo que consume el `<ng-select>` de plantilla. Se precalcula la etiqueta en
   * vez de componerla en la plantilla porque `bindLabel` apunta a UNA
   * propiedad y esa misma propiedad es sobre la que ng-select busca.
   */
  protected readonly templateOptions = computed(() =>
    this.templates().map((item) => ({
      templateId: item.templateId,
      label: `${item.name} (${item.code})`,
    })),
  );

  protected readonly form = this.formBuilder.nonNullable.group({
    templateId: ['', [Validators.required]],
    name: ['', [Validators.required, Validators.maxLength(150)]],
    status: [true],
  });

  protected readonly submitted = signal(false);
  protected readonly file = signal<File | null>(null);
  protected readonly preview = signal<string | null>(null);
  protected readonly fileError = signal<string | null>(null);

  protected readonly currentImage = computed(() => this.prefill()?.image?.urlFile ?? null);

  /** Carga el modal. `prefill` en `null` abre el formulario en modo alta. */
  init(prefill: PrefillModel | null, templates: TemplateModel[]): void {
    this.templates.set(templates);
    this.prefill.set(prefill);

    if (!prefill) return;

    this.form.patchValue({
      templateId: prefill.templateId,
      name: prefill.name,
      status: prefill.status,
    });

    // El API no actualiza la plantilla del prerellenado.
    this.form.controls.templateId.disable();
  }

  protected invalid(control: 'templateId' | 'name'): boolean {
    const field = this.form.controls[control];
    return field.invalid && (field.touched || this.submitted());
  }

  /**
   * Atributos que ng-select copia al `<input role="combobox">` interno, que es
   * el elemento que recibe el foco y, por tanto, el unico al que el lector de
   * pantalla le lee `aria-describedby` y `aria-invalid`. El `id` NO va aqui:
   * lo pone `[labelForId]`, que es lo que enlaza el `<label for>`.
   */
  protected templateInputAttrs(): Record<string, string> {
    return {
      'aria-describedby': this.invalid('templateId')
        ? 'ks-prefill-template-error'
        : 'ks-prefill-template-hint',
      'aria-invalid': this.invalid('templateId') ? 'true' : 'false',
    };
  }

  protected onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    if (!file) return;

    const error = validateUploadFile(file);

    if (error) {
      this.fileError.set(error);
      this.clearFile();
      input.value = '';
      return;
    }

    this.fileError.set(null);
    this.file.set(file);

    if (!isPreviewableImage(file.type)) {
      this.preview.set(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => this.preview.set(typeof reader.result === 'string' ? reader.result : null);
    reader.readAsDataURL(file);
  }

  protected clearFile(): void {
    this.file.set(null);
    this.preview.set(null);
  }

  protected save(): void {
    this.submitted.set(true);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const current = this.prefill();
    const file = this.file();

    const result: IPrefillModalResult = {
      templateId: current ? current.templateId : value.templateId,
      name: value.name.trim(),
      status: value.status,
      file,
      contentType: file ? file.type : null,
    };

    this.activeModal.close(result);
  }

  protected cancel(): void {
    this.activeModal.dismiss('cancel');
  }
}
